import { Platform } from 'react-native';

import {
  getGoogleClientId,
  GOOGLE_OAUTH_REDIRECT_SCHEME,
} from './environment';

const getNativeRedirectUri = (): string =>
  `${GOOGLE_OAUTH_REDIRECT_SCHEME}:/oauth`;

const getWebRedirectUri = (): string => {
  const location = (globalThis as { location?: { origin?: string } }).location;
  if (location?.origin) {
    return `${location.origin}/oauth`;
  }
  return 'http://localhost:3000/oauth';
};

export const getOAuthRedirectUri = (): string => {
  if (Platform.OS === ('web' as typeof Platform.OS)) return getWebRedirectUri();
  return getNativeRedirectUri();
};

export const GOOGLE_OAUTH_CONFIG = {
  get clientId() {
    const os = Platform.OS as string;
    const platform = os === 'ios' ? 'ios' : os === 'android' ? 'android' : 'web';
    return getGoogleClientId(platform);
  },
  get redirectUri() {
    return getOAuthRedirectUri();
  },
  scopes: ['openid', 'profile', 'email'] as const,
};

export const GOOGLE_OAUTH_URLS = {
  auth: 'https://accounts.google.com/o/oauth2/v2/auth',
  token: 'https://oauth2.googleapis.com/token',
};

const toBase64Url = (bytes: Uint8Array): string => {
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]!);
  }
  // Web / Hermes with btoa; PKCE is used on web today.
  const b64 = globalThis.btoa(binary);
  return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

/** Random PKCE code_verifier (43–128 chars). */
export function createPkceVerifier(): string {
  const bytes = new Uint8Array(32);
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i++) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }
  return toBase64Url(bytes);
}

/** S256 code_challenge from verifier (Web Crypto). */
export async function createPkceChallenge(verifier: string): Promise<string> {
  const data = new TextEncoder().encode(verifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return toBase64Url(new Uint8Array(digest));
}

export async function buildGoogleAuthUrl(
  state: string,
  redirectUri?: string,
  pkce?: { challenge: string },
): Promise<string> {
  const params = new URLSearchParams({
    client_id: GOOGLE_OAUTH_CONFIG.clientId,
    redirect_uri: redirectUri ?? GOOGLE_OAUTH_CONFIG.redirectUri,
    response_type: 'code',
    scope: GOOGLE_OAUTH_CONFIG.scopes.join(' '),
    state,
    access_type: 'offline',
    prompt: 'select_account',
  });

  if (pkce?.challenge) {
    params.set('code_challenge', pkce.challenge);
    params.set('code_challenge_method', 'S256');
  }

  return `${GOOGLE_OAUTH_URLS.auth}?${params.toString()}`;
}

export function extractAuthCodeFromUrl(url: string): string | null {
  try {
    const match = url.match(/[?&]code=([^&]+)/);
    return match ? decodeURIComponent(match[1]) : null;
  } catch {
    return null;
  }
}

export async function exchangeCodeForTokens(
  authCode: string,
  redirectUri?: string,
  codeVerifier?: string,
): Promise<{
  accessToken: string;
  refreshToken?: string;
  idToken: string;
}> {
  const body: Record<string, string> = {
    client_id: GOOGLE_OAUTH_CONFIG.clientId,
    code: authCode,
    grant_type: 'authorization_code',
    redirect_uri: redirectUri ?? GOOGLE_OAUTH_CONFIG.redirectUri,
  };
  if (codeVerifier) {
    body.code_verifier = codeVerifier;
  }

  const response = await fetch(GOOGLE_OAUTH_URLS.token, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: new URLSearchParams(body).toString(),
  });

  if (!response.ok) {
    const error = await response.text();
    throw new Error(`Token exchange failed: ${error}`);
  }

  const data = (await response.json()) as {
    access_token: string;
    refresh_token?: string;
    id_token: string;
  };

  if (!data.id_token) {
    throw new Error('Google token response missing id_token');
  }

  return {
    accessToken: data.access_token,
    refreshToken: data.refresh_token,
    idToken: data.id_token,
  };
}
